import { computed, isDevMode, Signal, signal, untracked } from '@angular/core';

import { signalMethod } from '@ngrx/signals';

const DEFAULT_PRESET_KEY = '__DEFAULT_PRESET__';

/** A key a registry resolves, and the preset it sits in — `null` for the default bucket. */
export interface RegistryKey {
  readonly key: string;
  readonly preset: string | null;
}

export class Registry<T> {
  /** Signal contains all registered presets. Key is a preset key and value is a registry. */
  readonly #dictionary = signal<Record<string, Record<string, T>>>({
    [DEFAULT_PRESET_KEY]: {},
  });

  /** Registry of the closest ancestor injector. Keys missing here are looked up there. */
  readonly #parent: Registry<T> | null;

  constructor(parent: Registry<T> | null = null) {
    this.#parent = parent;
  }

  getEntry(key: string, presetKey?: string): Signal<T> {
    return computed(() => {
      const entry = this.#lookup(key, presetKey);

      if (entry !== null) {
        return entry;
      }

      throw new Error(`Registry entry "${key}" is not registered!`);
    });
  }

  /**
   * Every key this registry resolves — its own, then its ancestors' — each key and preset once: a key held nearer hides
   * the same key and preset further up, as a lookup does. Signal-based, so an entry registered later shows.
   *
   * Annotated because the chain to the parent registry makes the type self-referential, which TypeScript cannot infer.
   */
  readonly keys: Signal<readonly RegistryKey[]> = computed(() => {
    const own = Object.entries(this.#dictionary()).flatMap(([presetKey, entries]) =>
      Object.keys(entries).map((key): RegistryKey => ({ key, preset: presetKey === DEFAULT_PRESET_KEY ? null : presetKey }))
    );
    const inherited = (this.#parent?.keys() ?? []).filter(
      (candidate) => !own.some(({ key, preset }) => key === candidate.key && preset === candidate.preset)
    );

    return [...own, ...inherited];
  });

  readonly registerPreset = signalMethod<{ presetKey: string; presetEntries: Record<string, T> }>(({ presetKey, presetEntries }) => {
    if (!presetKey) {
      throw new Error('Preset key is required for registration!');
    }

    if (isDevMode() && presetKey in this.#dictionary()) {
      console.warn(`Overriding existing preset "${presetKey}"!`);
    }

    untracked(() => {
      this.#dictionary.update((entries) => {
        return Object.assign({}, entries, { [presetKey]: presetEntries });
      });
    });
  });

  registerEntry(key: string, entry: T, presetKey: string = DEFAULT_PRESET_KEY): void {
    untracked(() => {
      if (isDevMode() && this.#getEntry(key, presetKey) !== null) {
        const inPreset = presetKey === DEFAULT_PRESET_KEY ? '' : ` in preset "${presetKey}"`;

        console.warn(`Overriding existing registry entry "${key}"${inPreset}!`);
      }

      this.#dictionary.update((presets) => {
        const updatedPreset = Object.assign({}, presets[presetKey], { [key]: entry });

        return Object.assign({}, presets, { [presetKey]: updatedPreset });
      });
    });
  }

  #getPreset(presetKey: string, nullable = false): Record<string, T> | null {
    const preset = this.#dictionary()[presetKey];

    if (preset) {
      return preset;
    }

    if (nullable) {
      return null;
    }

    throw new Error(`Preset "${presetKey}" is not registered!`);
  }

  #getEntry(key: string, presetKey: string): T | null {
    const preset = this.#getPreset(presetKey, true);

    return preset?.[key] ?? null;
  }

  /** Resolves within this registry first (preset, then default), then walks the ancestor chain. */
  #lookup(key: string, presetKey?: string): T | null {
    if (presetKey) {
      const presetEntry = this.#getEntry(key, presetKey);

      if (presetEntry !== null) {
        return presetEntry;
      }
    }

    const entry = this.#getEntry(key, DEFAULT_PRESET_KEY);

    if (entry !== null) {
      return entry;
    }

    return this.#parent === null ? null : this.#parent.#lookup(key, presetKey);
  }
}
